import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { DeleteRunDialog } from '../../components/common/DeleteRunDialog';
import { ModelTypeBadge } from '../../components/common/ModelTypeBadge';
import { RunTypeBadge } from '../../components/common/RunTypeBadge';
import { StatusBadge, STATUS_LABELS } from '../../components/common/StatusBadge';
import { resultSummary } from '../../metrics';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalRun, isRunTimedOut, RunStatus, RunType } from '../../types';
import { fmtIso } from '../../utils';

function ModelIds({ model_ids }: { model_ids: EvalRun['model_ids'] }) {
  const entries = (
    [
      ['OD', model_ids.OD],
      ['PR', model_ids.PR],
      ['PC', model_ids.PC],
    ] as const
  ).filter(([, id]) => id);
  if (!entries.length) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {entries.map(([type, id]) => (
        <div key={type} style={{ display: 'flex', alignItems: 'flex-start', gap: 5 }}>
          <ModelTypeBadge modelType={type} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, wordBreak: 'break-all' }}>
            {id!}
          </span>
        </div>
      ))}
    </div>
  );
}

const ALL_STATUSES = [
  { value: '', label: 'All statuses' },
  ...(Object.entries(STATUS_LABELS) as [RunStatus, string][]).map(([value, label]) => ({
    value,
    label,
  })),
];

const ALL_TYPES: Array<{ value: string; label: string }> = [
  { value: '', label: 'All types' },
  { value: 'Full', label: 'Full' },
  { value: 'OD', label: 'OD' },
  { value: 'PR', label: 'PR' },
  { value: 'PC', label: 'PC' },
];

function keyModelForSourceRun(sourceRun: EvalRun, forRunType: RunType): string | null {
  if (forRunType === 'PR') return sourceRun.model_ids.OD;
  if (forRunType === 'PC') return sourceRun.model_ids.PR;
  return null;
}

export const RunListPage: React.FC = () => {
  const { token } = useAuthContext();
  const navigate = useNavigate();
  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sourceRunMap, setSourceRunMap] = useState<Record<string, EvalRun>>({});

  useEffect(() => {
    setLoading(true);
    EvalApiService.listRuns(token ?? '').then(({ items }) => {
      const processed = items.map((run) => {
        if (isRunTimedOut(run)) {
          EvalApiService.timeoutRun(token ?? '', run.run_id);
          return {
            ...run,
            status: 'timed_out' as RunStatus,
            reason: 'Run timed out after 24h without progress',
          };
        }
        return run;
      });
      setRuns(processed);
      setLoading(false);

      const ids = [...new Set(processed.map((r) => r.source_run_id).filter(Boolean))] as string[];
      if (ids.length) {
        Promise.all(ids.map((id) => EvalApiService.getRun(token ?? '', id))).then((results) => {
          const map: Record<string, EvalRun> = {};
          results.forEach((r) => {
            if (r) map[r.run_id] = r;
          });
          setSourceRunMap(map);
        });
      }
    });
  }, [token]);

  const handleDelete = async () => {
    await Promise.all(Array.from(selected).map((id) => EvalApiService.deleteRun(token ?? '', id)));
    setRuns((prev) => prev.filter((r) => !selected.has(r.run_id)));
    setSelected(new Set());
    setShowDeleteDialog(false);
  };

  const filtered = runs.filter((r) => {
    if (statusFilter && r.status !== statusFilter) return false;
    if (typeFilter && r.run_type !== typeFilter) return false;
    return true;
  });

  return (
    <div className="page-content">
      {showDeleteDialog && (
        <DeleteRunDialog
          runIds={Array.from(selected)}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteDialog(false)}
        />
      )}
      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">Evaluation Runs</div>
          <div className="eval-page-sub">{runs.length} total runs</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selected.size >= 1 && (
            <button className="eval-btn-danger" onClick={() => setShowDeleteDialog(true)}>
              Delete ({selected.size})
            </button>
          )}
          {selected.size >= 2 && (
            <button
              className="eval-btn-warning"
              onClick={() => navigate(`/eval/runs/compare?runs=${Array.from(selected).join(',')}`)}
            >
              Compare ({selected.size})
            </button>
          )}
          <button className="eval-btn" onClick={() => navigate('/eval/runs/new')}>
            Schedule Run
          </button>
        </div>
      </div>

      <div className="eval-filters">
        <select
          className="eval-filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {ALL_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          className="eval-filter-select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          {ALL_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th>Run ID</th>
              <th>Type</th>
              <th>Status</th>
              <th>Dataset</th>
              <th>Source</th>
              <th>Models</th>
              <th>Triggered By</th>
              <th>Triggered At</th>
              <th>Result</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((run) => (
              <tr key={run.run_id}>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selected.has(run.run_id)}
                    disabled={!selected.has(run.run_id) && selected.size >= 4}
                    onChange={(e) => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        e.target.checked ? next.add(run.run_id) : next.delete(run.run_id);
                        return next;
                      });
                    }}
                  />
                </td>
                <td>
                  <Link
                    to={`/eval/runs/${run.run_id}`}
                    title={run.run_id}
                    style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}
                  >
                    {run.run_id.slice(-8)}
                  </Link>
                </td>
                <td>
                  <RunTypeBadge runType={run.run_type} />
                </td>
                <td>
                  <StatusBadge status={run.status} />
                </td>
                <td style={{ fontSize: 12, maxWidth: 160, overflowWrap: 'break-word' }}>
                  <Link to={`/eval/datasets/${run.dataset_id}`} style={{ color: 'var(--accent)' }}>
                    {run.dataset_name ?? run.dataset_id}
                  </Link>
                </td>
                <td style={{ fontSize: 12, maxWidth: 140, overflowWrap: 'break-word' }}>
                  {run.source_run_id ? (
                    (() => {
                      const srcRun = sourceRunMap[run.source_run_id];
                      const modelName = srcRun ? keyModelForSourceRun(srcRun, run.run_type) : null;
                      return (
                        <Link
                          to={`/eval/runs/${run.source_run_id}`}
                          style={{ color: 'var(--accent)' }}
                        >
                          {modelName ?? run.source_run_id.slice(-8)}
                          {modelName && (
                            <div
                              style={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: 10,
                                color: 'var(--text-tertiary)',
                                marginTop: 2,
                              }}
                            >
                              {run.source_run_id.slice(-8)}
                            </div>
                          )}
                        </Link>
                      );
                    })()
                  ) : (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '1px 6px',
                        borderRadius: 4,
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        background: 'var(--surface-raised)',
                        color: 'var(--text-secondary)',
                        border: '1px solid var(--border)',
                      }}
                    >
                      GT
                    </span>
                  )}
                </td>
                <td>
                  <ModelIds model_ids={run.model_ids} />
                </td>
                <td style={{ fontSize: 12 }}>{run.triggered_by.split('@')[0]}</td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {fmtIso(run.triggered_at)}
                </td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                  {resultSummary(run.result)}
                </td>
                <td
                  style={{
                    fontSize: 12,
                    maxWidth: 180,
                    color: run.comment ? 'var(--text-primary)' : 'var(--text-secondary)',
                  }}
                >
                  {run.comment ? (
                    <span title={run.comment}>
                      {run.comment.length > 60 ? run.comment.slice(0, 60) + '…' : run.comment}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={11}
                  style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}
                >
                  No runs match the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};
